'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { authApi, type ApiError } from '@/lib/api';
import styles from './register.module.css';

export default function RegisterPage() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    password: '',
    confirmPassword: '',
    industry: '',
    agreedToTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const industries = [
    {
      value: 'restaurant',
      label: 'Restaurant',
      img: '/images/restaurant.png',
      description: 'Perfect for restaurants and hospitality businesses',
      features: ['Order Taking', 'Reservations', 'Menu Inquiries']
    },
    {
      value: 'dental',
      label: 'Dental',
      img: '/images/dental.png',
      description: 'Designed for dental practices',
      features: ['Appointment Scheduling', 'Patient Inquiries', 'Insurance Questions']
    },
    {
      value: 'medical',
      label: 'Medical',
      img: '/images/medical.png',
      description: 'Designed for medical clinics and healthcare providers',
      features: ['Appointment Scheduling', 'Patient Inquiries', 'Insurance Questions']
    },
    {
      value: 'general',
      label: 'General Purpose',
      img: '/images/call_center.png',
      description: 'Flexible AI agent for any business type',
      features: ['Customer Support', 'Appointment Scheduling', 'Information & FAQs']
    },
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    } else if (formData.businessName.trim().length < 2) {
      newErrors.businessName = 'Business name must be at least 2 characters';
    } else if (formData.businessName.trim().length > 100) {
      newErrors.businessName = 'Business name must not exceed 100 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 12) {
      newErrors.password = 'Password must be at least 12 characters';
    } else if (formData.password.length > 128) {
      newErrors.password = 'Password must not exceed 128 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character (@$!%*?&)';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.industry) {
      newErrors.industry = 'Please select your industry';
    }

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const response = await authApi.register({
        email: formData.email,
        password: formData.password,
        businessName: formData.businessName,
        industry: formData.industry as any,
      });
      console.log('Registration successful:', response.message);
      router.push('/login?registered=true');
    } catch (err) {
      const apiError = err as ApiError;
      setErrors({ submit: apiError.message || 'Registration failed. Please try again.' });
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        {/* Header */}
        <div className={styles.cardHeader}>
          <div className={styles.logoWrap}>
            <Image src="/NovaMind-AI.png" alt="NovaMind AI" width={140} height={56} priority />
          </div>
          <h1 className={styles.cardTitle}>Create Account</h1>
          <p className={styles.cardSubtitle}>Join NovaMind AI today</p>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Submit error */}
          {errors.submit && (
            <div className={styles.errorBanner}>{errors.submit}</div>
          )}

          {/* Business Name */}
          <div className={styles.fieldGroup}>
            <label htmlFor="businessName" className={styles.label}>Business Name</label>
            <input
              id="businessName"
              type="text"
              value={formData.businessName}
              onChange={(e) => handleInputChange('businessName', e.target.value)}
              className={`${styles.input} ${errors.businessName ? styles.inputError : ''}`}
              placeholder="Your Business Name"
              disabled={isLoading}
            />
            {errors.businessName && <p className={styles.fieldError}>{errors.businessName}</p>}
          </div>

          {/* Email */}
          <div className={styles.fieldGroup}>
            <label htmlFor="email" className={styles.label}>Email Address</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              placeholder="you@company.com"
              disabled={isLoading}
            />
            {errors.email && <p className={styles.fieldError}>{errors.email}</p>}
          </div>

          {/* Industry */}
          <div className={styles.fieldGroup}>
            <span className={styles.industryLabel}>Select Your Industry</span>
            <div className={styles.industryGrid}>
              {industries.map((industry) => (
                <button
                  key={industry.value}
                  type="button"
                  onClick={() => handleInputChange('industry', industry.value)}
                  disabled={isLoading}
                  className={`${styles.industryCard} ${formData.industry === industry.value ? styles.industryCardActive : ''}`}
                >
                  {/* background image is data-driven per industry — must stay inline */}
                  <div className={styles.industryImg} style={{ backgroundImage: `url('${industry.img}')` }} />
                  <div className={styles.industryName}>{industry.label}</div>
                  {formData.industry === industry.value && (
                    <div className={styles.industryCheck}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
            {errors.industry && <p className={styles.fieldError}>{errors.industry}</p>}
          </div>

          {/* Password */}
          <div className={styles.fieldGroup}>
            <label htmlFor="password" className={styles.label}>Password</label>
            <div className={styles.inputWrap}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                placeholder="••••••••••••"
                disabled={isLoading}
              />
              <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <p className={styles.fieldError}>{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div className={styles.fieldGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>Confirm Password</label>
            <div className={styles.inputWrap}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                placeholder="••••••••••••"
                disabled={isLoading}
              />
              <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirmPassword(v => !v)} tabIndex={-1}>
                {showConfirmPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && <p className={styles.fieldError}>{errors.confirmPassword}</p>}
          </div>

          {/* Terms */}
          <div className={styles.termsRow}>
            <input
              type="checkbox"
              id="terms"
              checked={formData.agreedToTerms}
              onChange={(e) => handleInputChange('agreedToTerms', e.target.checked)}
              className={styles.termsCheckbox}
              disabled={isLoading}
            />
            <label htmlFor="terms" className={styles.termsText}>
              I agree to the{' '}
              <Link href="/terms" className={styles.termsLink}>Terms of Service</Link>
              {' '}and{' '}
              <Link href="/privacy" className={styles.termsLink}>Privacy Policy</Link>
            </label>
          </div>
          {errors.agreedToTerms && <p className={styles.fieldError}>{errors.agreedToTerms}</p>}

          {/* Submit */}
          <button type="submit" disabled={isLoading} className={styles.btnSubmit}>
            {isLoading ? (
              <span className={styles.btnSpinner}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className={styles.divider}>
          <div className={styles.dividerLine} />
          <span className={styles.dividerText}>Already have an account?</span>
          <div className={styles.dividerLine} />
        </div>

        {/* Sign in */}
        <Link href="/login" className={styles.btnLogin}>Sign In</Link>

        {/* Back to home */}
        <Link href="/" className={styles.backLink}>← Back to Home</Link>

      </div>
    </div>
  );
}
